import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription, forkJoin, interval, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AiOrderParserReviewService, isSupportedAiParserFile } from '../../../services/ai-order-parser-review/ai-order-parser-review.service';
import { AiOrderParserBatchListConfigService } from '../../../services/ai-order-parser-review/ai-order-parser-batch-list-config.service';
import { AiOrderParserItem } from '../../../models/ai-order-parser-item';

/** Every backend status → total count, for the quick-filter chips. */
type ParserStatusCounts = Record<string, number>;

@Component({
  selector: 'lib-pdf-ai-parser-batch-view',
  standalone: false,
  templateUrl: './pdf-ai-parser-batch-view.component.html',
  styleUrls: ['./pdf-ai-parser-batch-view.component.scss']
})
export class PdfAiParserBatchViewComponent implements OnInit, OnDestroy {
  private reviewService = inject(AiOrderParserReviewService);
  listConfigService = inject(AiOrderParserBatchListConfigService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  /** Free-text search bound to our own search box; pushed into the list service. */
  searchTerm = '';
  /** Page-size options for the inbox's own "Visa N" control (drives the `limit` query param). */
  readonly pageSizes = [25, 50, 100];
  pageSize = 25;

  /** How often the queue is re-fetched while the page is open (new emails + status changes). */
  private static readonly POLL_INTERVAL_MS = 30_000;
  /** How long the upload-success note stays before auto-dismissing. */
  private static readonly SUCCESS_NOTE_MS = 6_000;

  private subscriptions = new Subscription();
  private successNoteTimer?: ReturnType<typeof setTimeout>;

  // Only the dropzone elements (header panel / empty state) are drop targets;
  // this drives their drag-over highlight.
  isDragOver = false;

  // Manual upload strip
  isUploading = false;
  selectedFiles: File[] = [];
  /** Error per file from the last upload attempt; failed files stay in selectedFiles for retry. */
  fileErrors = new Map<File, string>();
  /** How many files the last upload attempt successfully queued (drives the auto-dismissing note). */
  lastUploadedCount = 0;
  /** Dismissible note shown when dropped/picked files were skipped as unsupported. */
  showSkippedNote = false;

  // Empty state: rendered by this component when the inbox has never had any orders.
  totalItems = 0;
  listLoaded = false;

  /** Total count per status, for the quick-filter chips. */
  statusCounts: ParserStatusCounts = {};

  ngOnInit(): void {
    // Singleton config service caches the last load; refresh on each visit so
    // status changes from approve/reject elsewhere are reflected.
    this.listConfigService.reload();
    // The queue moves without user interaction (emailed orders arrive, parse jobs
    // finish in the background) — keep it fresh while the page is open.
    this.subscriptions.add(
      interval(PdfAiParserBatchViewComponent.POLL_INTERVAL_MS).subscribe(() => this.listConfigService.reload())
    );
    this.subscriptions.add(
      this.listConfigService.getRawItems().subscribe(items => {
        this.totalItems = items.length;
        this.statusCounts = this.computeStatusCounts(items);
      })
    );
    this.subscriptions.add(this.listConfigService.loaded$.subscribe(loaded => (this.listLoaded = loaded)));
    this.subscriptions.add(
      this.route.queryParams.subscribe(qp => {
        const limit = Number(qp['limit']);
        this.pageSize = limit > 0 ? limit : this.pageSizes[0];
      })
    );
  }

  /** Apply the chosen page size via the `limit` query param gung-list reads. */
  onPageSizeChange(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { limit: this.pageSize, page: null },
      queryParamsHandling: 'merge'
    });
  }

  /** The status the chips currently filter by (owned by the list service). */
  get activeStatus(): string | null {
    return this.listConfigService.statusFilter;
  }

  /** Toggle a status quick-filter; clicking the active one clears it. */
  toggleStatusChip(status: string): void {
    this.listConfigService.setStatusFilter(this.activeStatus === status ? null : status);
  }

  /** Push our own search box's value into the list service's client-side filter. */
  onSearchChange(): void {
    this.listConfigService.setSearchTerm(this.searchTerm);
  }

  /** Total count per backend status (AWAITING_REVIEW, AWAITING_CONFIGURATION, DONE, REJECTED, ERROR). */
  private computeStatusCounts(items: AiOrderParserItem[]): ParserStatusCounts {
    const counts: ParserStatusCounts = {};
    for (const item of items) {
      if (item.status) {
        counts[item.status] = (counts[item.status] || 0) + 1;
      }
    }
    return counts;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    clearTimeout(this.successNoteTimer);
  }

  // --- Dropzone drag handling (header panel / empty state only) --------------

  onDragOver(event: DragEvent): void {
    event.preventDefault(); // required for the drop event to fire
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files) {
      this.addFiles(files);
    }
  }

  // --- File selection ---------------------------------------------------------

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(input.files);
    }
    input.value = ''; // allow picking the same file again
  }

  private addFiles(files: FileList): void {
    let skipped = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (isSupportedAiParserFile(file)) {
        this.selectedFiles.push(file);
      } else {
        skipped++;
      }
    }
    if (skipped > 0) {
      this.showSkippedNote = true;
    }
  }

  dismissSkippedNote(): void {
    this.showSkippedNote = false;
  }

  removeFile(index: number): void {
    this.fileErrors.delete(this.selectedFiles[index]);
    this.selectedFiles.splice(index, 1);
  }

  clearSelectedFiles(): void {
    this.selectedFiles = [];
    this.fileErrors.clear();
    this.showSkippedNote = false;
  }

  // --- Upload -----------------------------------------------------------------

  uploadFiles(): void {
    if (this.selectedFiles.length === 0) {
      return;
    }
    this.isUploading = true;
    this.lastUploadedCount = 0;
    // Each file is its own upload; a failure only marks that file, the rest still go through.
    forkJoin(
      this.selectedFiles.map(file =>
        this.reviewService.upload(file).pipe(
          map(item => ({ file, item, error: null as string | null })),
          catchError((err: HttpErrorResponse) =>
            of({ file, item: null, error: err.error?.message || `Upload failed (${err.statusText || err.status})` })
          )
        )
      )
    ).subscribe(results => {
      const failed = results.filter(r => r.error);
      this.fileErrors = new Map(failed.map(r => [r.file, r.error!]));
      this.selectedFiles = failed.map(r => r.file);
      this.lastUploadedCount = results.length - failed.length;
      this.isUploading = false;
      if (this.lastUploadedCount > 0) {
        clearTimeout(this.successNoteTimer);
        this.successNoteTimer = setTimeout(
          () => (this.lastUploadedCount = 0),
          PdfAiParserBatchViewComponent.SUCCESS_NOTE_MS
        );
      }
      // Show each successful upload immediately; the backend list read can lag behind the create.
      results.filter(r => r.item).forEach(r => this.listConfigService.addItem(r.item!));
      // Then reconcile with the backend (also refreshes anything else that changed).
      this.listConfigService.reload();
    });
  }
}
